
// frontend/src/components/ManagerTeamAttendance.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Popover,
  Avatar,
  Divider,
  Stack,
  Badge,
  alpha
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import {
  ChevronLeft,
  ChevronRight,
  Today,
  CheckCircle,
  Cancel,
  Warning,
  HolidayVillage,
  EventNote,
  Search,
  Download,
  Close,
  CalendarMonth,
  CalendarViewWeek,
  DateRange,
  CalendarToday,
  Schedule,
  People,
  TrendingUp,
  TrendingDown,
  AccessTime,
  CheckCircleOutline,
  RadioButtonUnchecked,
  RadioButtonChecked,
  FiberManualRecord,
  ExpandMore,
  FilterList
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import * as XLSX from 'xlsx';

dayjs.extend(utc);
dayjs.extend(timezone);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Helper function to format decimal hours to HH:MM
const formatTotalHours = (hours) => {
  if (!hours && hours !== 0) return '-';
  const hrs = Math.floor(hours);
  const mins = Math.round((hours - hrs) * 60);
  if (mins === 60) {
    return `${hrs + 1}:00`;
  }
  return `${hrs}:${mins.toString().padStart(2, '0')}`;
};

// Styled components for greytHR-like UI
const HeaderContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#fff',
  borderBottom: '1px solid #e0e0e0',
  padding: '16px 24px',
  marginBottom: '24px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '16px'
}));

const CalendarCard = styled(Paper)(({ theme }) => ({
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  border: '1px solid #e8e8e8'
}));

const DayHeaderCell = styled(TableCell)(({ theme, isweekend }) => ({
  backgroundColor: '#f8f9fa',
  fontWeight: 600,
  padding: '12px 8px',
  textAlign: 'center',
  borderBottom: '1px solid #e8e8e8',
  color: isweekend ? '#e53935' : '#333',
  fontSize: '13px'
}));

const EmployeeNameCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: '#fff',
  fontWeight: 500,
  padding: '12px 16px',
  borderBottom: '1px solid #e8e8e8',
  position: 'sticky',
  left: 0,
  backgroundColor: '#fff',
  zIndex: 2,
  minWidth: '200px'
}));

const AttendanceCell = styled(TableCell)(({ theme, status, isfuture }) => ({
  padding: '8px',
  textAlign: 'center',
  borderBottom: '1px solid #e8e8e8',
  backgroundColor: isfuture ? '#fafafa' : '#fff',
  cursor: 'pointer',
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: status === 'present' ? '#e8f5e9' : status === 'late' ? '#fff3e0' : '#ffebee',
  }
}));

const StatusBadge = styled(Box)(({ theme, status }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  // backgroundColor: 
  //   status === 'present' ? '#4caf50' :
  //   status === 'late' ? '#ff9800' :
  //   status === 'absent' ? '#f44336af' :
  //   status === 'holiday' ? '#9c27b0' :
  //   status === 'upcoming' ? '#9e9e9e' : '#e0e0e0',
  // color: '#fff',
  fontSize: '14px',
  fontWeight: 'bold',
  margin: '0 auto'
}));

const SummaryCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  border: '1px solid #e8e8e8',
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
  }
}));

const ViewToggleGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '8px',
  backgroundColor: '#f5f5f5',
  padding: '4px',
  borderRadius: '8px'
}));

const ViewButton = styled(Button)(({ theme, active }) => ({
  borderRadius: '6px',
  padding: '6px 16px',
  textTransform: 'none',
  fontWeight: 500,
  backgroundColor: active ? '#fff' : 'transparent',
  color: active ? '#1e88e5' : '#666',
  boxShadow: active ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
  '&:hover': {
    backgroundColor: active ? '#fff' : '#e0e0e0'
  }
}));

const SearchBar = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#fff',
    '&:hover fieldset': {
      borderColor: '#1e88e5'
    }
  }
}));

function ManagerTeamAttendance() {
  const [viewType, setViewType] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedPickerDate, setSelectedPickerDate] = useState(new Date());
  const [teamData, setTeamData] = useState([]);
  const [weeklyData, setWeeklyData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [yearlyData, setYearlyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDayDetails, setSelectedDayDetails] = useState(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);
  const [error, setError] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [companyConfig, setCompanyConfig] = useState({ dateFormat: 'DD/MM/YYYY', timezone: 'Asia/Kolkata' });

  const getManagerIdFromToken = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      try {
        const response = await axios.get(`${API_URL}/api/user/current?company_id=${company_id}`, {
          withCredentials: true
        });
        
        if (response.data && response.data.employee_id) {
          setEmployeeId(response.data.employee_id);

          try {
            const cmpRes = await axios.get(`${API_URL}/api/cmp/${company_id}`, { headers: { Authorization: `Bearer ${token}` } });
            if (cmpRes.data && cmpRes.data.data) {
              setCompanyConfig({
                dateFormat: cmpRes.data.data.dateFormat || 'DD/MM/YYYY',
                timezone: cmpRes.data.data.timezone || 'Asia/Kolkata'
              });
            }
          } catch(e) {}

          return response.data.employee_id;
        }
      } catch (err) {
      }
      
      if (payload.employee_id) {
        setEmployeeId(payload.employee_id);
        return payload.employee_id;
      }
      
      throw new Error('Employee ID not found');
    } catch (err) {
      //console.error("Error fetching current user:", err);
      setError('Failed to fetch user information');
      return null;
    }
  };

  const getCompanyIdFromToken = () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.company_id;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    if (viewType === 'week') {
      fetchWeeklyData();
    } else if (viewType === 'month') {
      fetchMonthlyData();
    } else if (viewType === 'year') {
      fetchYearlyData();
    }
  }, [viewType, selectedPickerDate, currentDate]);

  const fetchWeeklyData = async () => {
    setLoading(true);
    try {
      const manager_id = await getManagerIdFromToken();
      const company_id = getCompanyIdFromToken();
      const token = localStorage.getItem('token');
      if (!manager_id || !company_id) {
        showNotification('Authentication error', 'error');
        return;
      }

      const offsetMs = selectedPickerDate.getTimezoneOffset() * 60000;
      const localDate = new Date(selectedPickerDate.getTime() - offsetMs);
      const selectedDateStr = localDate.toISOString().split('T')[0];
      
      const response = await axios.get(
        `${API_URL}/api/attendance-requests/team-weekly/${manager_id}?selectedDate=${selectedDateStr}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          
          }
        }
      );
      
      if (response.data.success) {
        setWeeklyData(response.data.data);
        setTeamData(response.data.data.employees || []);
      }
    } catch (error) {
      //console.error('Error fetching weekly data:', error);
      showNotification('Failed to fetch team attendance', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const manager_id = await getManagerIdFromToken();
      const company_id = getCompanyIdFromToken();
      
      if (!manager_id || !company_id) {
        showNotification('Authentication error', 'error');
        return;
      }

      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      
      const response = await axios.get(
        `${API_URL}/api/attendance-requests/team-monthly/${manager_id}?year=${year}&month=${month}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
        
          }
        }
      );
      
      if (response.data.success) {
        setMonthlyData(response.data.data);
        setTeamData(response.data.data.employees || []);
      }
    } catch (error) {
      //console.error('Error fetching monthly data:', error);
      showNotification('Failed to fetch team attendance', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchYearlyData = async () => {
    setLoading(true);
    try {
      const manager_id = await getManagerIdFromToken();
      const company_id = getCompanyIdFromToken();
      const token = localStorage.getItem('token');
      if (!manager_id || !company_id) {
        showNotification('Authentication error', 'error');
        return;
      }

      const year = currentDate.getFullYear();
      
      const response = await axios.get(
        `${API_URL}/api/attendance-requests/team-yearly/${manager_id}?year=${year}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          }
        }
      );
      
      if (response.data.success) {
        setYearlyData(response.data.data);
      }
    } catch (error) {
      //console.error('Error fetching yearly data:', error);
      showNotification('Failed to fetch team attendance', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleDatePickerChange = (newDate) => {
    if (newDate) {
      setSelectedPickerDate(newDate);
      setCurrentDate(newDate);
    }
    setAnchorEl(null);
  };

  const changeWeek = (direction) => {
    const newDate = new Date(selectedPickerDate);
    newDate.setDate(selectedPickerDate.getDate() + (direction * 7));
    setSelectedPickerDate(newDate);
    setCurrentDate(newDate);
  };

  const changeMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const changeYear = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setFullYear(currentDate.getFullYear() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    const today = new Date();
    setSelectedPickerDate(today);
    setCurrentDate(today);
    setAnchorEl(null);
  };

  const handleCellClick = (employee, date, attendance) => {
    if (attendance?.isFutureDate === true) {
      showNotification('Cannot view details for future dates', 'info');
      return;
    }
    setSelectedEmployee(employee);
    setSelectedDayDetails({
      date,
      attendance,
      employee
    });
    setOpenDetailsDialog(true);
  };

  const exportToExcel = () => {
    let exportData = [];
    
    if (viewType === 'week' && weeklyData) {
      exportData = teamData.map(emp => {
        const row = { 'Employee Name': emp.employee_name, 'Designation': emp.designation || '-' };
        weeklyData.weekDays.forEach(day => {
          const att = emp.attendance[day];
          let statusText = att?.status?.toUpperCase() || 'ABSENT';
          if (att?.isFutureDate) statusText = 'UPCOMING';
          row[day] = statusText;
        });
        return row;
      });
    } else if (viewType === 'month' && monthlyData) {
      exportData = teamData.map(emp => {
        return { 
          'Employee Name': emp.employee_name, 
          'Designation': emp.designation || '-',
          'Present': emp.stats.present,
          'Late': emp.stats.late,
          'Absent': emp.stats.absent,
          'Attendance Rate': `${emp.stats.attendanceRate}%`
        };
      });
    }
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Team Attendance');
    XLSX.writeFile(wb, `team_attendance_${viewType}_${new Date().toISOString().split('T')[0]}.xlsx`);
    showNotification('Export completed successfully', 'success');
  };

  const getWeekRangeText = () => {
    if (!weeklyData) return '';
    return `${dayjs(weeklyData.weekStart).tz(companyConfig.timezone).format(companyConfig.dateFormat)} - ${dayjs(weeklyData.weekEnd).tz(companyConfig.timezone).format(companyConfig.dateFormat)}`;
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'present': return 'Present';
      case 'late': return 'Late';
      case 'absent': return 'Absent';
      case 'holiday': return 'Holiday';
      case 'upcoming': return 'Upcoming';
      default: return 'Unknown';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'present': return <CheckCircle sx={{ fontSize: 20 }} />;
      case 'late': return <AccessTime sx={{ fontSize: 20 }} />;
      case 'absent': return <Cancel sx={{ fontSize: 20 }} />;
      case 'holiday': return <HolidayVillage sx={{ fontSize: 20 }} />;
      default: return <RadioButtonUnchecked sx={{ fontSize: 20 }} />;
    }
  };

  const filteredEmployees = teamData.filter(emp => 
    emp.employee_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.employee_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDatePickerClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDatePickerClose = () => {
    setAnchorEl(null);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const filterOpen = Boolean(filterAnchorEl);

  // Calculate summary stats
  const getSummaryStats = () => {
    if (viewType === 'week' && weeklyData) {
      return {
        totalPresent: weeklyData.summary?.totalPresent || 0,
        totalLate: weeklyData.summary?.totalLate || 0,
        totalAbsent: weeklyData.summary?.totalAbsent || 0,
        totalHoliday: weeklyData.summary?.totalHoliday || 0,
        totalEmployees: teamData.length
      };
    }
    if (viewType === 'month' && monthlyData) {
      const totalPresent = teamData.reduce((sum, emp) => sum + (emp.stats?.present || 0), 0);
      const totalLate = teamData.reduce((sum, emp) => sum + (emp.stats?.late || 0), 0);
      const totalAbsent = teamData.reduce((sum, emp) => sum + (emp.stats?.absent || 0), 0);
      const avgAttendance = teamData.length > 0 
        ? Math.round(teamData.reduce((sum, emp) => sum + (emp.stats?.attendanceRate || 0), 0) / teamData.length)
        : 0;
      return {
        totalPresent,
        totalLate,
        totalAbsent,
        avgAttendance,
        totalEmployees: teamData.length
      };
    }
    return null;
  };

  const stats = getSummaryStats();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // Get day of week (0 = Sunday, 6 = Saturday)
  const isWeekend = (date) => {
    const day = new Date(date).getDay();
    return day === 0 || day === 6;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="xl" sx={{ mt: 0, mb: 4, px: { xs: 2, sm: 3 } }}>
        {/* Header with greytHR style */}
        <HeaderContainer>
          <Box>
            <Typography variant="h4"  fontWeight="bold">
              Team Attendance
            </Typography>
               <Typography variant="subtitle1" color="textSecondary">
              Manage and monitor your team's attendance
            </Typography>
            {/* <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
              {viewType === 'week' && weeklyData && `${weeklyData.monthName} ${weeklyData.year}`}
              {viewType === 'month' && monthlyData && `${monthlyData.monthName} ${monthlyData.year}`}
              {viewType === 'year' && yearlyData && `Year ${yearlyData.year}`}
              {' • '}Default Attendance Cycle
            </Typography> */}
          </Box>
          
          <Box display="flex" gap={2} alignItems="center">
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={exportToExcel}
              sx={{ borderRadius: '8px', textTransform: 'none' }}
            >
              Export
            </Button>
          </Box>
        </HeaderContainer>

        {/* Summary Cards */}
        {stats && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3} md={2.4}>
              <SummaryCard>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="caption" color="textSecondary">Total Present</Typography>
                      <Typography variant="h5" fontWeight="bold" color="#4caf50">{stats.totalPresent}</Typography>
                    </Box>
                    <CheckCircleOutline sx={{ fontSize: 32, color: '#4caf50', opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </SummaryCard>
            </Grid>
            <Grid item xs={6} sm={3} md={2.4}>
              <SummaryCard>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="caption" color="textSecondary">Late Arrivals</Typography>
                      <Typography variant="h5" fontWeight="bold" color="#ff9800">{stats.totalLate}</Typography>
                    </Box>
                    <AccessTime sx={{ fontSize: 32, color: '#ff9800', opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </SummaryCard>
            </Grid>
            <Grid item xs={6} sm={3} md={2.4}>
              <SummaryCard>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="caption" color="textSecondary">Absent</Typography>
                      <Typography variant="h5" fontWeight="bold" color="#f44336">{stats.totalAbsent}</Typography>
                    </Box>
                    <Cancel sx={{ fontSize: 32, color: '#f44336', opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </SummaryCard>
            </Grid>
            {stats.totalHoliday !== undefined && (
              <Grid item xs={6} sm={3} md={2.4}>
                <SummaryCard>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="caption" color="textSecondary">Holidays</Typography>
                        <Typography variant="h5" fontWeight="bold" color="#9c27b0">{stats.totalHoliday}</Typography>
                      </Box>
                      <HolidayVillage sx={{ fontSize: 32, color: '#9c27b0', opacity: 0.7 }} />
                    </Box>
                  </CardContent>
                </SummaryCard>
              </Grid>
            )}
            {stats.avgAttendance !== undefined && (
              <Grid item xs={6} sm={3} md={2.4}>
                <SummaryCard>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="caption" color="textSecondary">Avg Attendance</Typography>
                        <Typography variant="h5" fontWeight="bold" color="#1e88e5">{stats.avgAttendance}%</Typography>
                      </Box>
                      <TrendingUp sx={{ fontSize: 32, color: '#1e88e5', opacity: 0.7 }} />
                    </Box>
                  </CardContent>
                </SummaryCard>
              </Grid>
            )}
            <Grid item xs={6} sm={3} md={2.4}>
              <SummaryCard>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="caption" color="textSecondary">Team Size</Typography>
                      <Typography variant="h5" fontWeight="bold">{stats.totalEmployees}</Typography>
                    </Box>
                    <People sx={{ fontSize: 32, color: '#757575', opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </SummaryCard>
            </Grid>
          </Grid>
        )}

        {/* View Toggle and Search */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: '12px' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <ViewToggleGroup>
              <ViewButton
                active={viewType === 'week'}
                onClick={() => setViewType('week')}
                startIcon={<CalendarViewWeek />}
              >
                Weekly
              </ViewButton>
              <ViewButton
                active={viewType === 'month'}
                onClick={() => setViewType('month')}
                startIcon={<CalendarMonth />}
              >
                Monthly
              </ViewButton>
              <ViewButton
                active={viewType === 'year'}
                onClick={() => setViewType('year')}
                startIcon={<DateRange />}
              >
                Yearly
              </ViewButton>
            </ViewToggleGroup>
            
            <Box display="flex" alignItems="center" gap={1.5}>
              <SearchBar
                size="small"
                placeholder="Search employee..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20, color: '#999' }} /></InputAdornment>
                }}
                sx={{ width: 260 }}
              />
              <Tooltip title="Filter by status">
                <IconButton onClick={handleFilterClick} size="small">
                  <FilterList />
                </IconButton>
              </Tooltip>
              <Chip 
                label={statusFilter === 'all' ? 'All Status' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                size="small"
                onDelete={statusFilter !== 'all' ? () => setStatusFilter('all') : null}
                sx={{ ml: 0.5 }}
              />
            </Box>
          </Box>
        </Paper>

        {/* Status Filter Popover */}
        <Popover
          open={filterOpen}
          anchorEl={filterAnchorEl}
          onClose={handleFilterClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ p: 2, minWidth: 150 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Filter by Status</Typography>
            <Stack spacing={1}>
              {['all', 'present', 'late', 'absent', 'upcoming'].map((status) => (
                <Button
                  key={status}
                  size="small"
                  fullWidth
                  sx={{ justifyContent: 'flex-start', textTransform: 'capitalize' }}
                  onClick={() => {
                    setStatusFilter(status);
                    handleFilterClose();
                  }}
                  variant={statusFilter === status ? 'contained' : 'text'}
                >
                  {status === 'all' ? 'All' : status}
                </Button>
              ))}
            </Stack>
          </Box>
        </Popover>

        {/* Navigation with Date Picker - Weekly View */}
        {viewType === 'week' && (
          <Paper sx={{ p: 2, mb: 3, borderRadius: '12px' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <IconButton onClick={() => changeWeek(-1)} sx={{ bgcolor: '#f5f5f5', '&:hover': { bgcolor: '#e0e0e0' } }}>
                <ChevronLeft />
              </IconButton>
              
              <Box textAlign="center">
                <Button
                  variant="outlined"
                  startIcon={<CalendarToday />}
                  onClick={handleDatePickerClick}
                  sx={{ borderRadius: 8, px: 3, py: 1, textTransform: 'none' }}
                >
                  <Typography variant="subtitle1" fontWeight="500">
                    {getWeekRangeText()}
                  </Typography>
                </Button>
              </Box>
              
              <Box>
                <Button onClick={goToToday} startIcon={<Today />} sx={{ mr: 1, textTransform: 'none' }}>
                  Today
                </Button>
                <IconButton onClick={() => changeWeek(1)} sx={{ bgcolor: '#f5f5f5', '&:hover': { bgcolor: '#e0e0e0' } }}>
                  <ChevronRight />
                </IconButton>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Navigation for Monthly/Yearly View */}
        {viewType === 'month' && monthlyData && (
          <Paper sx={{ p: 2, mb: 3, borderRadius: '12px' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <IconButton onClick={() => changeMonth(-1)} sx={{ bgcolor: '#f5f5f5' }}>
                <ChevronLeft />
              </IconButton>
              
              <Box textAlign="center">
                <Typography variant="h6" fontWeight="500">
                  {monthlyData.monthName} {monthlyData.year}
                </Typography>
                <Button size="small" onClick={goToToday} startIcon={<Today />} sx={{ textTransform: 'none' }}>
                  Today
                </Button>
              </Box>
              
              <IconButton onClick={() => changeMonth(1)} sx={{ bgcolor: '#f5f5f5' }}>
                <ChevronRight />
              </IconButton>
            </Box>
          </Paper>
        )}

        {viewType === 'year' && yearlyData && (
          <Paper sx={{ p: 2, mb: 3, borderRadius: '12px' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <IconButton onClick={() => changeYear(-1)} sx={{ bgcolor: '#f5f5f5' }}>
                <ChevronLeft />
              </IconButton>
              
              <Box textAlign="center">
                <Typography variant="h6" fontWeight="500">
                  Year {yearlyData.year}
                </Typography>
                <Button size="small" onClick={goToToday} startIcon={<Today />} sx={{ textTransform: 'none' }}>
                  Today
                </Button>
              </Box>
              
              <IconButton onClick={() => changeYear(1)} sx={{ bgcolor: '#f5f5f5' }}>
                <ChevronRight />
              </IconButton>
            </Box>
          </Paper>
        )}

        {/* Date Picker Popover */}
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleDatePickerClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Box sx={{ p: 2 }}>
            <DatePicker
              value={selectedPickerDate}
              onChange={handleDatePickerChange}
              slotProps={{
                textField: { size: 'small', fullWidth: true }
              }}
            />
            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button size="small" onClick={goToToday}>Today</Button>
              <Button size="small" onClick={handleDatePickerClose}>Cancel</Button>
            </Box>
          </Box>
        </Popover>

        {/* Weekly View Table - greytHR style */}
        {viewType === 'week' && weeklyData && (
          <CalendarCard>
            <Box sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <DayHeaderCell sx={{ minWidth: 200, position: 'sticky', left: 0, zIndex: 3 }}>
                      Employee name
                    </DayHeaderCell>
                    <DayHeaderCell align="center" sx={{ minWidth: 60 }}>P</DayHeaderCell>
                    {weeklyData.weekDays?.map((day, idx) => {
                      const dayDate = new Date(day);
                      const isWeekendDay = isWeekend(day);
                      return (
                        <DayHeaderCell key={idx} align="center" isweekend={isWeekendDay ? 1 : 0}>
                          {dayjs(day).tz(companyConfig.timezone).format('ddd')}
                          <Typography variant="caption" display="block" color={isWeekendDay ? '#e53935' : '#999'}>
                            {dayjs(day).tz(companyConfig.timezone).date()}
                          </Typography>
                        </DayHeaderCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEmployees.map((employee) => {
                    // Calculate present count (excluding holidays and upcoming)
                    const presentCount = weeklyData.weekDays?.filter(day => {
                      const att = employee.attendance[day];
                      return att?.status === 'present' || att?.status === 'late';
                    }).length || 0;
                    
                    return (
                      <TableRow key={employee.employee_id} hover>
                        <EmployeeNameCell>
                          <Box>
                            <Typography variant="body2" fontWeight="500">
                              {employee.employee_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" fontSize="11px">
                              {employee.designation || 'Employee'}
                            </Typography>
                          </Box>
                        </EmployeeNameCell>
                        <TableCell align="center" sx={{ bgcolor: '#fafafa', fontWeight: 500 }}>
                          {presentCount}
                        </TableCell>
                        {weeklyData.weekDays?.map((day, idx) => {
                          const attendance = employee.attendance[day];
                          const isFuture = attendance?.isFutureDate === true;
                          const status = attendance?.status || 'absent';
                          const isVisible = statusFilter === 'all' || statusFilter === status;
                          
                          if (!isVisible) return <AttendanceCell key={idx} status={status} isfuture={isFuture} />;
                          
                          let statusDisplay = '';
                          let bgColor = '';
                          if (status === 'present') {
                            statusDisplay = 'P';
                            bgColor = '#4caf50';
                          } else if (status === 'late') {
                            statusDisplay = 'L';
                            bgColor = '#ff9800';
                          } else if (status === 'absent') {
                            statusDisplay = 'A';
                            bgColor = '#f44336';
                          } else if (status === 'holiday') {
                            statusDisplay = 'H';
                            bgColor = '#9c27b0';
                          } else if (status === 'upcoming' || isFuture) {
                            statusDisplay = '-';
                            bgColor = '#9e9e9e';
                          } else {
                            statusDisplay = 'R';
                            bgColor = '#e0e0e0';
                          }
                          
                          return (
                            <AttendanceCell 
                              key={idx} 
                              status={status} 
                              isfuture={isFuture}
                              onClick={() => handleCellClick(employee, day, attendance)}
                            >
                              <Tooltip title={`${getStatusText(status)} - Click for details`}>
                                <StatusBadge status={status}>
                                  {statusDisplay}
                                </StatusBadge>
                              </Tooltip>
                            </AttendanceCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </CalendarCard>
        )}

        {/* Monthly View Table */}
        {viewType === 'month' && monthlyData && (
          <CalendarCard>
            <Box sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Employee name</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>P</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>L</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>A</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>H</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Attendance %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEmployees.map((employee) => (
                    <TableRow key={employee.employee_id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="500">
                            {employee.employee_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {employee.designation || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={employee.stats?.present || 0} size="small" sx={{ bgcolor: '#e8f5e9', fontWeight: 500 }} />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={employee.stats?.late || 0} size="small" sx={{ bgcolor: '#fff3e0', fontWeight: 500 }} />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={employee.stats?.absent || 0} size="small" sx={{ bgcolor: '#ffebee', fontWeight: 500 }} />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={employee.stats?.holiday || 0} size="small" sx={{ bgcolor: '#f3e5f5', fontWeight: 500 }} />
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={`${employee.stats?.attendanceRate || 0}%`} 
                          size="small"
                          sx={{ 
                            bgcolor: (employee.stats?.attendanceRate || 0) >= 90 ? '#e8f5e9' : 
                                     (employee.stats?.attendanceRate || 0) >= 75 ? '#fff3e0' : '#ffebee',
                            fontWeight: 500,
                            color: (employee.stats?.attendanceRate || 0) >= 90 ? '#2e7d32' : 
                                   (employee.stats?.attendanceRate || 0) >= 75 ? '#ed6c02' : '#d32f2f'
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </CalendarCard>
        )}

        {/* Yearly View Table */}
        {viewType === 'year' && yearlyData && (
          <CalendarCard>
            <Box sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Month</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Total Present</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Total Late</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Total Absent</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Avg Attendance %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {yearlyData.monthlySummary?.map((month, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>
                        <Typography fontWeight="500">{month.monthName}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={month.totalPresent} size="small" sx={{ bgcolor: '#e8f5e9' }} />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={month.totalLate} size="small" sx={{ bgcolor: '#fff3e0' }} />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={month.totalAbsent} size="small" sx={{ bgcolor: '#ffebee' }} />
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={`${month.averageAttendance}%`}
                          size="small"
                          sx={{ 
                            bgcolor: month.averageAttendance >= 90 ? '#e8f5e9' : 
                                     month.averageAttendance >= 75 ? '#fff3e0' : '#ffebee',
                            color: month.averageAttendance >= 90 ? '#2e7d32' : 
                                   month.averageAttendance >= 75 ? '#ed6c02' : '#d32f2f'
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </CalendarCard>
        )}

        {/* Quick Links / Employee Avatars section - like greytHR */}
        {teamData.length > 0 && (
          <Paper sx={{ mt: 3, p: 2, borderRadius: '12px', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="textSecondary" fontWeight={500}>Quick Links:</Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {teamData.slice(0, 8).map((emp, idx) => (
                <Tooltip key={idx} title={emp.employee_name}>
                  <Avatar 
                    sx={{ 
                      width: 32, 
                      height: 32, 
                      bgcolor: '#1e88e5', 
                      fontSize: '14px',
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.8 }
                    }}
                    onClick={() => {
                      setSearchTerm(emp.employee_name);
                    }}
                  >
                    {emp.employee_name?.charAt(0).toUpperCase()}
                  </Avatar>
                </Tooltip>
              ))}
            </Box>
          </Paper>
        )}

        {/* Details Dialog */}
        <Dialog open={openDetailsDialog} onClose={() => setOpenDetailsDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
          <DialogTitle sx={{ pb: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight="600">Attendance Details</Typography>
              <IconButton onClick={() => setOpenDetailsDialog(false)} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent>
            {selectedEmployee && selectedDayDetails && (
              <Box>
                <Box mb={3} p={2} bgcolor="#f5f5f5" borderRadius={2}>
                  <Typography variant="caption" color="textSecondary">Employee</Typography>
                  <Typography variant="h6" fontWeight="500">{selectedEmployee.employee_name}</Typography>
                  <Typography variant="body2" color="textSecondary">{selectedEmployee.designation || 'Employee'}</Typography>
                </Box>
                
                <Box mb={3} p={2} bgcolor="#f5f5f5" borderRadius={2}>
                  <Typography variant="caption" color="textSecondary">Date</Typography>
                  <Typography variant="h6" fontWeight="500">
                    {dayjs(selectedDayDetails.date).tz(companyConfig.timezone).format('dddd, MMMM D, YYYY')}
                  </Typography>
                </Box>
                
                {selectedDayDetails.attendance?.status === 'upcoming' || selectedDayDetails.attendance?.isFutureDate ? (
                  <Box textAlign="center" py={3}>
                    <EventNote sx={{ fontSize: 64, color: '#9e9e9e', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">Upcoming Date</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Attendance for future dates will be recorded when the date arrives.
                    </Typography>
                  </Box>
                ) : selectedDayDetails.attendance?.status === 'holiday' ? (
                  <Box textAlign="center" py={3}>
                    <HolidayVillage sx={{ fontSize: 64, color: '#9c27b0', mb: 2 }} />
                    <Typography variant="h6" color="#9c27b0">Holiday</Typography>
                    <Typography variant="body2">
                      {selectedDayDetails.attendance.details?.holiday || 'Company Holiday'}
                    </Typography>
                  </Box>
                ) : selectedDayDetails.attendance?.details ? (
                  <>
                    <Box mb={3} p={2} borderRadius={2} sx={{ bgcolor: selectedDayDetails.attendance.status === 'present' ? '#e8f5e9' : '#fff3e0' }}>
                      <Typography variant="caption" color="textSecondary">Status</Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getStatusIcon(selectedDayDetails.attendance.status)}
                        <Typography variant="h6" textTransform="uppercase" fontWeight="500">
                          {getStatusText(selectedDayDetails.attendance.status)}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                          <Typography variant="caption" color="textSecondary">Check In</Typography>
                          <Typography variant="h6" fontWeight="500">
                            {selectedDayDetails.attendance.details.in_time || '-'}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                          <Typography variant="caption" color="textSecondary">Check Out</Typography>
                          <Typography variant="h6" fontWeight="500">
                            {selectedDayDetails.attendance.details.out_time || '-'}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                    
                    {selectedDayDetails.attendance.details.total_hours && (
                      <Box mt={2} p={2} bgcolor="#e3f2fd" borderRadius={2} textAlign="center">
                        <Typography variant="caption" color="textSecondary">Total Hours</Typography>
                        <Typography variant="h5" fontWeight="bold">
                          <Schedule sx={{ fontSize: 20, verticalAlign: 'middle', mr: 1 }} />
                          {selectedDayDetails.attendance.details.formatted_total_hours || 
                           formatTotalHours(selectedDayDetails.attendance.details.total_hours)} hrs
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <Box textAlign="center" py={3}>
                    <Cancel sx={{ fontSize: 64, color: '#f44336', mb: 2 }} />
                    <Typography variant="h6" color="error">Absent</Typography>
                    <Typography variant="body2" color="textSecondary">No attendance record found for this date.</Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenDetailsDialog(false)} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ borderRadius: 2 }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
}

export default ManagerTeamAttendance;